/**
 * Admin -- Audit/Integrity (Wave 1b W1b-32)
 *
 * Hash chain status, async full-chain verification job, and the WORM
 * witness records list. Top banner surfaces the broken-chain state from
 * the last 100-row quick walk.
 *
 * Backend: /api/v1/admin/audit/integrity (W1b-32).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ShieldCheck,
  ShieldAlert,
  Hash,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Copy,
  PlayCircle,
  Loader2,
  KeyRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { relativeTime } from '@/lib/format';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { useHasPermission } from '@/lib/use-has-permission';

export const Route = createFileRoute('/admin/audit/integrity')({
  component: IntegrityPage,
});

interface IntegrityStatus {
  chainHeadSeq: number;
  chainHeadHash: string;
  chainHeadExists: boolean;
  lastWitnessAt: string | null;
  lastWitnessSeq: number | null;
  isHealthy: boolean;
  brokenChainBanner: { active: boolean; brokenAt?: number; reason?: string };
  witnessKeyId: string;
}

interface VerifyJob {
  status: 'running' | 'complete' | 'failed';
  startedAt: string;
  completedAt: string | null;
  verified: boolean | null;
  brokenAt: number | null;
  walked: number | null;
  brokenReason: string | null;
  error: string | null;
}

interface WitnessRecord {
  id: string;
  organizationId: string;
  chainHeadSeq: string;
  chainHeadHash: string;
  witnessSignature: string;
  witnessPubkeyId: string;
  witnessedAt: string;
  syncedToExternal: boolean;
  externalWitnessUrl: string | null;
}

interface SignatureVerifyResult {
  valid: boolean;
  errorIfAny: string | null;
  verifiedAt: string;
  signingMessage: string;
}

function IntegrityPage() {
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [bannerAcknowledged, setBannerAcknowledged] = useState(false);
  const [signatureResult, setSignatureResult] =
    useState<{ id: string; result: SignatureVerifyResult } | null>(null);

  // Pod-QQ — Fixes #649: pre-fix the Verify sig button was rendered for
  // every viewer of /admin/audit/integrity. The backend route
  // `POST /admin/audit/integrity/witness-records/:id/verify-signature`
  // requires the OWNER-only `delete:audit` permission, so tenant
  // ADMINs (and lower roles) clicking the button got a bare 403 with
  // no actionable explanation. Per the bug ticket's suggested fix
  // ("Either grant the intended tenant-admin audit permission for this
  // read/forensic verification path, OR hide/disable Verify sig with a
  // role explanation"), we hide the button for callers who lack the
  // permission. The matrix in `@argusai/shared/schemas/permissions` is
  // the single source of truth — same matrix the API enforces, so
  // there is no UI-vs-API drift.
  const canVerifyWitnessSignature = useHasPermission('delete', 'audit');

  const statusQuery = useQuery({
    queryKey: ['audit', 'integrity', 'status'],
    queryFn: () => apiClient.get<IntegrityStatus>('/admin/audit/integrity/status'),
    refetchInterval: 30_000,
  });

  const witnessQuery = useQuery({
    queryKey: ['audit', 'integrity', 'witness-records'],
    queryFn: () =>
      apiClient.get<{ items: WitnessRecord[]; total: number }>(
        '/admin/audit/integrity/witness-records?limit=100'
      ),
  });

  const verifyJobQuery = useQuery({
    queryKey: ['audit', 'integrity', 'verify-now', activeJobId],
    queryFn: () =>
      activeJobId
        ? apiClient.get<VerifyJob>(
            `/admin/audit/integrity/verify-now/${activeJobId}`
          )
        : Promise.reject(new Error('no jobId')),
    enabled: !!activeJobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll while running, stop on terminal state.
      if (!data) return 1000;
      return data.status === 'running' ? 1000 : false;
    },
  });

  const startVerify = useMutation({
    mutationFn: () =>
      apiClient.post<{ jobId: string }>('/admin/audit/integrity/verify-now', {
        maxRecords: 1000,
      }),
    onSuccess: (data) => {
      setActiveJobId(data.jobId);
      toast.success('Chain verification started');
    },
    onError: (err) => {
      toast.error(`Failed to start verification: ${(err as Error).message}`);
    },
  });

  const syncWitness = useMutation({
    mutationFn: () =>
      apiClient.post<{
        recordId: string | null;
        chainHeadSeq: number;
        chainHeadHash: string;
        skipped: boolean;
        skippedReason: string | null;
      }>('/admin/audit/integrity/witness-records/sync'),
    onSuccess: (data) => {
      if (data.skipped) {
        toast.info(`Witness sync skipped: ${data.skippedReason}`);
      } else {
        toast.success(`Witness record created at seq ${data.chainHeadSeq}`);
      }
      witnessQuery.refetch();
      statusQuery.refetch();
    },
    onError: (err) => {
      toast.error(`Witness sync failed: ${(err as Error).message}`);
    },
  });

  const verifySignature = useMutation({
    mutationFn: (id: string) =>
      apiClient.post<SignatureVerifyResult>(
        `/admin/audit/integrity/witness-records/${id}/verify-signature`
      ),
    onSuccess: (result, id) => {
      setSignatureResult({ id, result });
      if (result.valid) {
        toast.success('Signature valid');
      } else {
        toast.error(`Signature INVALID: ${result.errorIfAny}`);
      }
    },
    onError: (err) => {
      toast.error(`Signature verify failed: ${(err as Error).message}`);
    },
  });

  function handleCopy(value: string, label: string) {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied to clipboard`);
  }

  const banner = statusQuery.data?.brokenChainBanner;
  const showBanner = banner?.active && !bannerAcknowledged;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5">
      {/* Broken chain banner */}
      {showBanner && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-md p-4 space-y-2">
          <div className="flex items-center gap-2 font-medium text-red-700 dark:text-red-400">
            <ShieldAlert className="h-5 w-5" />
            Hash chain BROKEN at sequence {banner!.brokenAt}
          </div>
          <p className="text-xs text-muted-foreground">
            Reason: <code>{banner!.reason ?? 'unknown'}</code>. The audit
            chain has been tampered with or is missing a row. Run a full
            verification to find the exact divergence, then restore from
            the most recent backup before the break.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBannerAcknowledged(true)}
          >
            Acknowledge & continue
          </Button>
        </div>
      )}

      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Hash Chain Integrity
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Tamper-evident audit ledger. Every event is hash-chained; daily
          witness records bind chain head to an Ed25519 signature for
          regulator-facing attestation.
        </p>
      </div>

      {/* Status grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
              <Hash className="h-3 w-3" />
              Chain head
            </div>
            <div>
              <div className="text-3xl font-semibold tabular-nums">
                {statusQuery.data?.chainHeadSeq.toLocaleString() ?? '—'}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                events recorded
              </div>
            </div>
            {statusQuery.data?.chainHeadExists && (
              <div className="space-y-1">
                <Label className="text-xs">Chain head hash</Label>
                <div className="flex items-start gap-2">
                  <code className="text-[11px] font-mono break-all flex-1">
                    {statusQuery.data.chainHeadHash}
                  </code>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 shrink-0"
                    onClick={() =>
                      handleCopy(statusQuery.data!.chainHeadHash, 'Chain head hash')
                    }
                    title="Copy chain head hash"
                    aria-label="Copy chain head hash"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
              <KeyRound className="h-3 w-3" />
              External witness
            </div>
            <div>
              {statusQuery.data?.lastWitnessAt ? (
                <>
                  <div className="text-sm font-medium">
                    Last attestation {relativeTime(statusQuery.data.lastWitnessAt)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Bound to chain seq{' '}
                    {statusQuery.data.lastWitnessSeq?.toLocaleString()}
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No witness records yet — first attestation will fire
                  within 24 hours, or sync now.
                </div>
              )}
            </div>
            {statusQuery.data?.witnessKeyId && (
              <div className="text-xs">
                <span className="text-muted-foreground">Platform key:</span>{' '}
                <code className="font-mono">{statusQuery.data.witnessKeyId}</code>
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              className="w-full gap-1.5"
              onClick={() => syncWitness.mutate()}
              disabled={syncWitness.isPending}
              title="Manually create a witness record now (requires security.privileged)"
            >
              {syncWitness.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Re-sync now
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Verify-now */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Verify the entire chain</div>
              <p className="text-xs text-muted-foreground mt-1">
                Walks up to 1000 records from the genesis row. Records the
                outcome in audit_chain so the verification itself is auditable.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => startVerify.mutate()}
              disabled={startVerify.isPending}
              className="gap-1.5"
            >
              <PlayCircle className="h-3 w-3" />
              Verify now
            </Button>
          </div>

          {activeJobId && verifyJobQuery.data && (
            <div className="border rounded p-3 mt-3 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                {verifyJobQuery.data.status === 'running' && (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Walking chain…
                  </>
                )}
                {verifyJobQuery.data.status === 'complete' &&
                  verifyJobQuery.data.verified && (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      Chain intact -- {verifyJobQuery.data.walked} records verified
                    </>
                  )}
                {verifyJobQuery.data.status === 'complete' &&
                  !verifyJobQuery.data.verified && (
                    <>
                      <XCircle className="h-4 w-4 text-red-500" />
                      Chain BROKEN at sequence {verifyJobQuery.data.brokenAt}
                    </>
                  )}
                {verifyJobQuery.data.status === 'failed' && (
                  <>
                    <XCircle className="h-4 w-4 text-red-500" />
                    Verification failed: {verifyJobQuery.data.error}
                  </>
                )}
              </div>
              {verifyJobQuery.data.brokenReason && (
                <div className="text-xs text-muted-foreground">
                  Reason: <code>{verifyJobQuery.data.brokenReason}</code>
                </div>
              )}
              <div className="text-[10px] text-muted-foreground">
                Job {activeJobId.slice(0, 8)}… • started{' '}
                {relativeTime(verifyJobQuery.data.startedAt)}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Witness records */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-medium">Witness records (WORM)</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Write-once-read-many attestations of chain head. Daily by
              default. The DB rejects any UPDATE or DELETE attempt.
            </p>
          </div>
          <Badge variant="outline" className="text-[10px]">
            {witnessQuery.data?.total ?? 0} records
          </Badge>
        </div>

        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr className="text-left">
                <th className="p-2 font-medium">Witnessed</th>
                <th className="p-2 font-medium text-right">Chain seq</th>
                <th className="p-2 font-medium">Chain head hash</th>
                <th className="p-2 font-medium">Signature</th>
                <th className="p-2 font-medium">Pubkey</th>
                <th className="p-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(witnessQuery.data?.items ?? []).map((w) => (
                <tr key={w.id} className="border-t hover:bg-muted/30">
                  <td className="p-2 whitespace-nowrap">
                    <div>{relativeTime(w.witnessedAt)}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(w.witnessedAt).toLocaleString()}
                    </div>
                  </td>
                  <td className="p-2 text-right font-mono">{w.chainHeadSeq}</td>
                  <td className="p-2">
                    <div className="flex items-center gap-1">
                      <code className="font-mono text-[10px]">
                        {w.chainHeadHash.slice(0, 16)}…
                      </code>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-5 w-5"
                        onClick={() =>
                          handleCopy(w.chainHeadHash, 'Chain head hash')
                        }
                        title="Copy chain head hash"
                        aria-label="Copy chain head hash"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                  <td className="p-2">
                    <div className="flex items-center gap-1">
                      <code className="font-mono text-[10px]">
                        {w.witnessSignature.slice(0, 12)}…
                      </code>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-5 w-5"
                        onClick={() =>
                          handleCopy(w.witnessSignature, 'Signature')
                        }
                        title="Copy signature"
                        aria-label="Copy signature"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                  <td className="p-2 font-mono text-[10px]">
                    {w.witnessPubkeyId}
                  </td>
                  <td className="p-2">
                    {canVerifyWitnessSignature ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-[11px] gap-1"
                        onClick={() => verifySignature.mutate(w.id)}
                        disabled={verifySignature.isPending}
                        title="Re-verify Ed25519 signature (owner-only)"
                      >
                        <ShieldCheck className="h-3 w-3" />
                        Verify sig
                      </Button>
                    ) : (
                      // Pod-QQ #649: hide the action for non-owners. The
                      // explanatory hint mirrors the row-level lock copy
                      // elsewhere in the admin UI so the user knows what
                      // permission they would need to invoke it.
                      <span
                        className="text-[10px] text-muted-foreground italic"
                        title="Requires the owner-only delete:audit permission. Re-verification is a regulator-facing forensic action."
                      >
                        owner only
                      </span>
                    )}
                    {signatureResult?.id === w.id && (
                      <div className="mt-1 text-[10px]">
                        {signatureResult.result.valid ? (
                          <span className="text-emerald-500 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Valid
                          </span>
                        ) : (
                          <span className="text-red-500 flex items-center gap-1">
                            <XCircle className="h-3 w-3" />
                            Invalid
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {witnessQuery.data?.items?.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="p-6 text-center text-muted-foreground"
                  >
                    No witness records yet. The first attestation will run
                    automatically within 24 hours, or click "Re-sync now"
                    above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Separator />
      <div className="text-[11px] text-muted-foreground space-y-1">
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Witness cadence: every 24 hours, skip if chain has not advanced
          since the last attestation.
        </div>
        <div className="flex items-center gap-1">
          <Hash className="h-3 w-3" />
          Hash algorithm: SHA-256 over canonical
          <code className="font-mono">
            sequence|event_type|actor_id|resource_type|resource_id|event_data|created_at|previous_hash
          </code>
        </div>
        <div className="flex items-center gap-1">
          <KeyRound className="h-3 w-3" />
          Signature algorithm: Ed25519 over{' '}
          <code className="font-mono">seq|hash|witnessedAt</code>
        </div>
      </div>
    </div>
  );
}
